import { useState } from 'react';
import { apiCall, Api, round, mergeExpandedPoints, getCurveLabel, createCurve, isFilterReady, getDefaultFilter, isFilterValuesWrong, needAutoFillFilter, shouldAutoFillFilterProperty, track, getGearboxInertiaSubtitutedMax, getLoadInertiaMax, getN2Max, getNumberOfStartupsMax, getChangedFilter, getNonFilterTags, isFilterValueValid, getChangedTags } from '../../services';
import { DEFAULT_FREQUENCY, FILTER_PROPERTIES_MAP, INITIAL_MES_REQUEST, INITIAL_TAGS, REMOVABLE_TAG, STARTING_CONSTRAINTS, TRANSLATIONS } from '../../constants';
import { useAppDispatch, useAppSelector, useTranslate } from '../common';
import { setCalculationResponse, setCalculationSuccessful, setDiagramData, setFilter, setFilterValues, setHasMotor, setMesRequest, setTags, toggleUpdateFilters, toggleLoading, setLoadInertiaHighDialog, setGearboxInertiaDialog, setLoadInertiaLowDialog, setGearboxInputData, setVsdOperationType, setNumberOfStartupsDialog, resetFilter, removeTags, setAcText, setAwText, setRenderLineOperations, setRenderConverterOperation, setDisableOperationalData, addBanner, toggleMotorSelectionAccordion, resetBanners } from '../../store';
import { Curve, CurveCalculationResult, CurveData, Filter, FilterValues, LanguageValue, MesRequest, StartingConstraintsCondition, Tags } from '../../models';
import { MessageType, StartingType, VsdOperationType } from '../../enums';
import { BannerType } from '../../components/common/Banner';
import { showStatusMessage } from '@innomotics/innomotics-build-react-lib';

export const useFilter = () => {
    const dispatch = useAppDispatch();
    const translate = useTranslate();
    const tags = useAppSelector(state => state.motor.tags);
    const filter = useAppSelector(state => state.motor.filter);
    const filterValues = useAppSelector(state => state.motor.filterValues);
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const isStartingTypeDOL = mesRequest.commonData.startingType === StartingType.DOL;
    const clientMotorRequest = useAppSelector(state => state.motor.clientMotorResponse);
    const motorSelectionAccordionOpen = useAppSelector(state => state.motor.motorSelectionAccordionOpen);

    const loadInitialData = async () => {
        dispatch(toggleLoading());

        let filterResponse: Filter = filter;
        let filterValuesResponse: FilterValues = await getInitialFilterValues(filter);

        while (needAutoFillFilter(filterResponse, filterValuesResponse)) {
            filterResponse = autoFillFilter(filterResponse, filterValuesResponse);
            filterValuesResponse = await getInitialFilterValues(filterResponse);
        }

        dispatch(setRenderLineOperations(true));

        dispatch(toggleLoading());
    };

    const loadInitialDtkData = async () => {
        dispatch(toggleLoading());

        const response = await loadCalculationData(mesRequest);

        initVsdOperationSelector(response);

        track(
            'MotorSelection', {
            mlfb: mesRequest.commonData.mlfb
        }
        );

        dispatch(toggleLoading());
    };

    const initVsdOperationSelector = (calculationResponse: CurveCalculationResult) => {
        if (calculationResponse) {
            const { isDolOperation, isVsdOperation } = calculationResponse.mesParameters.inputData.vsdMotorData;

            if (isDolOperation && isVsdOperation) {
                dispatch(setVsdOperationType(VsdOperationType.LineAndConverterOperationVSD));
                dispatch(setRenderLineOperations(true));
                dispatch(setRenderConverterOperation(true));
            } else if ((isDolOperation && !isVsdOperation) || (!isDolOperation && !isVsdOperation)) {
                dispatch(setRenderLineOperations(true));
            } else if (!isDolOperation && isVsdOperation) {
                dispatch(setRenderConverterOperation(true));
                dispatch(setDisableOperationalData(true));
            }
        }
    };

    const getInitialFilterValues = async (filter: Filter) => {
        let response: FilterValues = filterValues;

        await apiCall(
            Api.getFilterValues(filter),
            async x => {
                response = x.data;
                dispatch(setFilterValues(x.data));
            },
            async () => {
                showStatusMessage({
                    message: translate(TRANSLATIONS.snackbar.error.filter.text),
                    showProgress: true,
                    type: 'error'
                });
            }
        );

        return response;
    };

    const isAccordionIncomplete = (...accordionValues: (undefined | number | string)[]) => !motorSelectionAccordionOpen && accordionValues.some(x => !isFilterValueValid(x));

    const getAllCurves = async (mesRequest: MesRequest) => {
        let response: CurveCalculationResult | undefined = undefined;

        await apiCall(
            Api.calculateAllCurves(mesRequest),
            async x => {
                if (x.data && x.data.loadOperationValues && x.data.loadOperationValues.jExternalMaxACold) {
                    x.data.loadOperationValues.jExternalMaxACold = round(x.data.loadOperationValues.jExternalMaxACold, 4);
                }

                if (x.data && x.data.loadOperationValues && x.data.loadOperationValues.jExternalMaxAWarm) {
                    x.data.loadOperationValues.jExternalMaxAWarm = round(x.data.loadOperationValues.jExternalMaxAWarm, 4);
                }

                if (x.data && x.data.loadOperationValues && x.data.loadOperationValues.gearboxInertiaMaxCold) {
                    x.data.loadOperationValues.gearboxInertiaMaxCold = round(x.data.loadOperationValues.gearboxInertiaMaxCold, 4);
                }

                if (x.data && x.data.loadOperationValues && x.data.loadOperationValues.gearboxInertiaMaxWarm) {
                    x.data.loadOperationValues.gearboxInertiaMaxWarm = round(x.data.loadOperationValues.gearboxInertiaMaxWarm, 4);
                }

                setErrorBanners(x.data);

                dispatch(setCalculationResponse(x.data));

                response = x.data;
            },
            async () => {
                showStatusMessage({
                    message: translate(TRANSLATIONS.snackbar.error.allCurves.text),
                    showProgress: true,
                    type: 'error'
                });
            }
        );

        return response;
    };

    const setErrorBanners = (response: CurveCalculationResult) => {
        const errorMessages = response && !response.isConfirmed && response.messages &&
            response.messages.filter(x => x.type === MessageType.Error);

        dispatch(resetBanners());

        errorMessages && Boolean(errorMessages.length) &&
            errorMessages.map(x =>
                dispatch(addBanner({
                    text: translate((TRANSLATIONS.errorMessages.curveCalculation as never)[x.code]),
                    type: BannerType.Error,
                    closeable: true
                }))
            );
    };

    const correctCalculationData = async (
        mesRequest: MesRequest,
        calculationResponse: CurveCalculationResult,
        isWrongFunction: (mesRequest: MesRequest, calculationResponse: CurveCalculationResult, condition?: StartingConstraintsCondition) => boolean,
        correctFunction: (mesRequest: MesRequest, calculationResponse: CurveCalculationResult, condition?: StartingConstraintsCondition) => MesRequest,
        tag: keyof Tags
    ) => {
        let correctionAttemptCount = 1;
        let newMesRequest = mesRequest;
        let newCalculationData: CurveCalculationResult | undefined;
        let condition: StartingConstraintsCondition | undefined = undefined;

        if (tag == STARTING_CONSTRAINTS.coldCondition || tag == STARTING_CONSTRAINTS.warmCondition) {
            condition = tag;
        }

        dispatch(setTags({ [tag]: undefined }));

        do {
            newMesRequest = await correctFunction(newMesRequest, calculationResponse, condition);
            newCalculationData = await getAllCurves(newMesRequest);
        } while (isWrongFunction(newMesRequest, calculationResponse, condition) && correctionAttemptCount++ < 10);

        return { newMesRequest, newCalculationData };
    };

    const isGearboxInertiaWrong = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult) => {
        const { startingCondition } = mesRequest.commonData;
        const { gearboxInertia } = mesRequest.gearboxData;

        const gearboxInertiaMax = getGearboxInertiaSubtitutedMax(calculationResponse, startingCondition);

        return Boolean(gearboxInertiaMax) && Number(gearboxInertia) > gearboxInertiaMax;
    };

    const isN2Wrong = (mesRequest: MesRequest) => {
        const n2Max = mesRequest.motorData.speed && getN2Max(mesRequest.motorData.speed);
        const { n2 } = mesRequest.gearboxData;

        return Boolean(n2Max) && Number(n2) > Number(n2Max);
    };

    const updateLoadInertia = (mesRequest: MesRequest) => {
        const { loadInertia } = mesRequest.gearboxData;

        dispatch(setLoadInertiaLowDialog(Number(loadInertia) < 0));
        loadInertia && dispatch(setGearboxInputData({ loadInertia: loadInertia.toString() }));
    };

    const updateN2 = (mesRequest: MesRequest) => {
        const { n2 } = mesRequest.gearboxData;

        n2 && dispatch(setGearboxInputData({ n2: n2.toString() }));
    };

    const updateGearboxInertia = (mesRequest: MesRequest) => {
        const { gearboxInertia } = mesRequest.gearboxData;

        dispatch(setGearboxInertiaDialog(Number(gearboxInertia) < 0));
        gearboxInertia && dispatch(setGearboxInputData({ gearboxInertia: gearboxInertia.toString() }));
    };


    const correctGearboxInertia = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult): MesRequest => {
        const { startingCondition } = mesRequest.commonData;
        const gearboxInertiaMax = calculationResponse ? getGearboxInertiaSubtitutedMax(calculationResponse, startingCondition) : null;
        const newMesRequest = { ...mesRequest, gearboxData: { ...mesRequest.gearboxData, gearboxInertia: gearboxInertiaMax } };

        dispatch(setMesRequest(newMesRequest));
        updateGearboxInertia(newMesRequest);

        return newMesRequest;
    };

    const correctN2 = (mesRequest: MesRequest) => {
        const { speed } = mesRequest.motorData;
        const n2Max = speed && getN2Max(speed);
        const newMesRequest = { ...mesRequest, gearboxData: { ...mesRequest.gearboxData, n2: n2Max } };

        dispatch(setMesRequest(newMesRequest));
        updateN2(newMesRequest);

        return newMesRequest;
    };

    const correctLoadInertia = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult): MesRequest => {
        const { startingCondition } = mesRequest.commonData;
        const loadInertiaMax = getLoadInertiaMax(calculationResponse, startingCondition);
        const newMesRequest = { ...mesRequest, gearboxData: { ...mesRequest.gearboxData, loadInertia: loadInertiaMax } };

        dispatch(setMesRequest(newMesRequest));
        updateLoadInertia(newMesRequest);

        return newMesRequest;
    };

    const isLoadInertiaWrong = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult) => {
        const { startingCondition } = mesRequest.commonData;
        const { loadInertia } = mesRequest.gearboxData;
        const loadInertiaMax = getLoadInertiaMax(calculationResponse, startingCondition);

        return Boolean(loadInertiaMax) && Number(loadInertia) > Number(loadInertiaMax);
    };

    const isNumberOfStartupsWrong = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult, condition?: StartingConstraintsCondition) => {
        if (condition) {
            const value = mesRequest.commonData && mesRequest.commonData[condition] as number;
            const numbersOfStartupMax = getNumberOfStartupsMax(calculationResponse, condition);

            return Boolean(numbersOfStartupMax) && value > Number(numbersOfStartupMax);
        } else return true;
    };

    const correctNumberOfStartups = (mesRequest: MesRequest, calculationResponse: CurveCalculationResult, condition?: StartingConstraintsCondition): MesRequest => {
        let newMesRequest = mesRequest;

        if (condition) {
            const numbersOfStartupMax = getNumberOfStartupsMax(calculationResponse, condition);
            newMesRequest = { ...mesRequest, commonData: { ...mesRequest.commonData, [condition]: numbersOfStartupMax } };

            condition === STARTING_CONSTRAINTS.coldCondition
                ? dispatch(setAcText(numbersOfStartupMax ? numbersOfStartupMax.toString() : ''))
                : dispatch(setAwText(numbersOfStartupMax ? numbersOfStartupMax.toString() : ''));

            dispatch(setMesRequest(newMesRequest));
        }

        return newMesRequest;
    };

    const parseCurves = (mesRequest: MesRequest, curveData: CurveData[]) => {
        return curveData.reduce((curves, curveData) => {
            const { startingVoltage, startingType } = mesRequest.commonData;
            const { type, expandedSpecialPoints } = curveData;
            const { xPoints, yPoints } = curveData.expandedPoints;

            const mergedExpandedSpecialPoint = mergeExpandedPoints(expandedSpecialPoints);
            const label = getCurveLabel(type, startingVoltage, startingType, translate);
            const curve = createCurve(label, type, xPoints, yPoints, mergedExpandedSpecialPoint, isStartingTypeDOL);

            return [...curves, curve];
        }, [] as Curve[]);
    };

    const loadCalculationData = async (mesRequest: MesRequest) => {
        let newMesRequest = mesRequest;

        let calculationResponse = await getAllCurves(newMesRequest) as unknown as CurveCalculationResult;

        if (calculationResponse) {
            if (isGearboxInertiaWrong(newMesRequest, calculationResponse)) {
                const correctedData = await correctCalculationData(newMesRequest, calculationResponse, isGearboxInertiaWrong, correctGearboxInertia, 'gearboxInertia');

                newMesRequest = correctedData.newMesRequest;
                calculationResponse = correctedData.newCalculationData ?? calculationResponse;
            }

            if (isN2Wrong(newMesRequest)) {
                const correctedData = await correctCalculationData(newMesRequest, calculationResponse, isN2Wrong, correctN2, 'n2');

                newMesRequest = correctedData.newMesRequest;
                calculationResponse = correctedData.newCalculationData ?? calculationResponse;
            }

            if (isLoadInertiaWrong(newMesRequest, calculationResponse)) {
                const correctedData = await correctCalculationData(newMesRequest, calculationResponse, isLoadInertiaWrong, correctLoadInertia, 'loadInertia');

                newMesRequest = correctedData.newMesRequest;
                calculationResponse = correctedData.newCalculationData ?? calculationResponse;

                dispatch(setLoadInertiaHighDialog(true));
            }

            if (isNumberOfStartupsWrong(newMesRequest, calculationResponse, STARTING_CONSTRAINTS.coldCondition)) {
                const correctedData = await correctCalculationData(newMesRequest, calculationResponse, isNumberOfStartupsWrong, correctNumberOfStartups, STARTING_CONSTRAINTS.coldCondition);

                newMesRequest = correctedData.newMesRequest;
                calculationResponse = correctedData.newCalculationData ?? calculationResponse;

                newMesRequest.commonData.startingCondition === 0 && dispatch(setNumberOfStartupsDialog(true));
            }

            if (isNumberOfStartupsWrong(newMesRequest, calculationResponse, STARTING_CONSTRAINTS.warmCondition)) {
                const correctedData = await correctCalculationData(newMesRequest, calculationResponse, isNumberOfStartupsWrong, correctNumberOfStartups, STARTING_CONSTRAINTS.warmCondition);

                newMesRequest = correctedData.newMesRequest;
                calculationResponse = correctedData.newCalculationData ?? calculationResponse;

                newMesRequest.commonData.startingCondition === 1 && dispatch(setNumberOfStartupsDialog(true));
            }

            const isCalculationSuccess =
                calculationResponse && calculationResponse.curveData.every(curveData =>
                    curveData.points.xPoints.every(x => !isNaN(x)) && curveData.points.yPoints.every(y => !isNaN(y))
                );

            const diagramData = isCalculationSuccess && parseCurves(newMesRequest, calculationResponse.curveData);

            dispatch(setCalculationSuccessful(isCalculationSuccess));
            diagramData && dispatch(setDiagramData(diagramData));

            const infoMessages = calculationResponse.messages && calculationResponse.messages.filter(x => x.type === MessageType.Info);

            if (infoMessages) {
                infoMessages.map(x => {
                    showStatusMessage({
                        message: translate((TRANSLATIONS.infoMessages.curveCalculation as never)[x.code.toString()]),
                        showProgress: true,
                        type: 'info'
                    });
                });
            }
        }

            return calculationResponse;
        };

        const loadFilterValues = async (filter: Filter): Promise<FilterValues | undefined> => {
            let response;

            await apiCall(
                Api.getFilterValues(filter),
                async x => {
                    dispatch(setFilterValues(x.data));

                    response = x.data;
                },
                async () => {
                    showStatusMessage({
                        message: translate(TRANSLATIONS.snackbar.error.filter.text),
                        showProgress: true,
                        type: 'error'
                    });
                }
            );

            return response;
        };

        const loadMesRequest = async (filter: Filter) => {
            let response;

            await apiCall(
                Api.getMotorRequest(filter),
                async x => {
                    response = x.data;
                    dispatch(setMesRequest(x.data));
                    dispatch(toggleUpdateFilters());
                },
                async () => {
                    showStatusMessage({
                        message: translate(TRANSLATIONS.snackbar.error.filter.text),
                        showProgress: true,
                        type: 'error'
                    });
                }
            );

            return response;
        };

        const correctFilter = (frequency: number) => {
            const newFilter = getDefaultFilter(frequency);

            dispatch(setTags(INITIAL_TAGS));
            dispatch(setFilter(newFilter));

            return newFilter;
        };

        const autoFillFilter = (filter: Filter, filterValues: FilterValues) => {
            const subset: Partial<Filter> = {};

            (Object.keys(filterValues) as Array<keyof FilterValues>).forEach(x => {
                if (shouldAutoFillFilterProperty(x, filter, filterValues)) {
                    const [value] = filterValues[x];

                    (subset[FILTER_PROPERTIES_MAP[x]] as string | number | LanguageValue) = value;
                }
            });

            dispatch(setFilter(subset));

            return getChangedFilter(filter, subset);
        };

        const handlePreselectedReset = (defaultValue: any, currentValues: any[], filter: Partial<Filter>, calledByTag = false) => {
            dispatch(toggleLoading());

            !calledByTag && dispatch(setTags({ [Object.keys(filter)[0]]: null }));

            if (currentValues.some(x => x === defaultValue)) {
                handleFilterChange(filter, tags);
            } else {
                handleFilterChange({ ...REMOVABLE_TAG, ...filter }, REMOVABLE_TAG, undefined, true);
            }

            dispatch(toggleLoading());
        };

        const removeNonFilterChips = (tags: Tags | undefined) => {
            const currentNonFilterTags = tags && getNonFilterTags.filter(x => Object.keys(tags).some(y => y === x)) as (keyof Tags)[];
            const nonFilterTags: Tags = {};

            currentNonFilterTags && currentNonFilterTags.forEach(nonFilterTag => {
                nonFilterTags[nonFilterTag] = undefined;
            });

            dispatch(setFilter(nonFilterTags));
        };

        const handleHasMotor = async (value: boolean) => {
            dispatch(setHasMotor(value));

            if (motorSelectionAccordionOpen !== !value) {
                dispatch(toggleMotorSelectionAccordion(!motorSelectionAccordionOpen));
            }

            if (!value) {
                dispatch(setCalculationResponse(undefined));
                dispatch(setMesRequest(INITIAL_MES_REQUEST));
                dispatch(setDiagramData(null));
                dispatch(toggleUpdateFilters());
            }
        };

        const handleFilterChange = async (subset: Partial<Filter>, newTags?: Partial<Tags>, shouldAutoFillFilter = true, shouldCheckFilter = false, shouldResetFilter = false) => {
            let newFilter = filter;

            newTags && dispatch(setTags(newTags));
            removeNonFilterChips(newTags ? getChangedTags(tags ?? {}, newTags) : tags);

            if (shouldResetFilter) {
                newFilter = getDefaultFilter(DEFAULT_FREQUENCY);
                dispatch(resetFilter(newFilter));
                dispatch(removeTags());
            }

            newFilter = getChangedFilter(newFilter, subset);
            let newFilterValues = filterValues;

            const isPreviousFilterReady = isFilterReady(filter);

            dispatch(setFilter(newFilter));

            newFilterValues = await loadFilterValues(newFilter) ?? newFilterValues;

            if (shouldCheckFilter && isFilterValuesWrong(filterValues)) {
                newFilter = correctFilter(newFilter.frequency);

                newFilterValues = await loadFilterValues(newFilter) ?? newFilterValues;
            }

            while (shouldAutoFillFilter && needAutoFillFilter(newFilter, newFilterValues)) {
                newFilter = autoFillFilter(newFilter, newFilterValues);

                newFilterValues = await loadFilterValues(newFilter) ?? newFilterValues;
            }

            const isCurrentFilterReady = isFilterReady(newFilter);

            if (isCurrentFilterReady) {
                const newMesRequest = await loadMesRequest(newFilter) ?? mesRequest;
                await loadCalculationData(newMesRequest);
                handleHasMotor(true);

                !clientMotorRequest && track('MotorSelection', { mlfb: newFilter.mlfb });
            }

            if (isPreviousFilterReady && !isCurrentFilterReady) {
                handleHasMotor(false);
            }
        };

        return {
            loadInitialData,
            loadInitialDtkData,
            onFilterChange: handleFilterChange,
            onPreselectedReset: handlePreselectedReset,
            getCalculationData: loadCalculationData,
            isAccordionIncomplete: isAccordionIncomplete,
            autoFillFilter
        };
    };
